import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Logo from '@/components/Logo';

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      {/* Header */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="glass-card border-b border-white/10 sticky top-0 z-50"
      >
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Logo textClassName="text-2xl font-bold text-white" />
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-white hover:text-blue-300 hover:bg-white/10 transition-all"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </motion.header>

      {/* Content */}
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass-card-white p-8 md:p-12 rounded-2xl"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Conditions Générales d'Utilisation</h1>
          <p className="text-gray-600 mb-8">Dernière mise à jour : Décembre 2024</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Company Info */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Informations sur l'Entreprise</h2>
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                <p className="font-semibold text-gray-900 mb-2">MARKEAP</p>
                <p className="text-gray-700">Propriété de MY DIGITAL IDENTITY LTD</p>
                <p className="text-gray-700">71-75 Shelton Street, Covent Garden</p>
                <p className="text-gray-700">London, United Kingdom, WC2H 9JQ</p>
              </div>
            </section>

            {/* Acceptance */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Acceptation des Conditions</h2>
              <p>
                En accédant et en utilisant la plateforme Markeap ("le Service"), vous acceptez d'être lié par ces 
                Conditions Générales d'Utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser 
                notre Service.
              </p>
            </section>

            {/* Description */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Description du Service</h2>
              <p className="mb-4">
                Markeap est une plateforme SaaS de marketing intelligent qui offre :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Jeu de la roue cadeaux interactif pour la fidélisation client</li>
                <li>Génération automatique de réponses aux avis clients par IA</li>
                <li>Plateforme de marketing par SMS et email</li>
                <li>Optimisation SEO automatisée par IA</li>
                <li>Publication multi-réseaux sociaux</li>
              </ul>
            </section>

            {/* Account */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Compte Utilisateur</h2>
              <p className="mb-4">
                Pour utiliser Markeap, vous devez créer un compte. Vous êtes responsable de :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Maintenir la confidentialité de vos identifiants de connexion</li>
                <li>Toutes les activités qui se produisent sous votre compte</li>
                <li>Nous notifier immédiatement de toute utilisation non autorisée</li>
                <li>Fournir des informations exactes et à jour</li>
              </ul>
            </section>

            {/* Subscription */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Abonnement et Paiement</h2>
              <p className="mb-4">
                <strong>5.1 Plans d'abonnement :</strong> Markeap propose des plans d'abonnement mensuels. 
                Le prix actuel est de 89€/mois (offre promotionnelle).
              </p>
              <p className="mb-4">
                <strong>5.2 Facturation :</strong> Les frais d'abonnement sont facturés mensuellement à l'avance. 
                Le paiement est traité automatiquement à chaque date d'anniversaire.
              </p>
              <p className="mb-4">
                <strong>5.3 Renouvellement automatique :</strong> Votre abonnement se renouvelle automatiquement 
                à moins que vous ne l'annuliez avant la date de renouvellement.
              </p>
              <p className="mb-4">
                <strong>5.4 Crédits SMS/Email :</strong> 30 crédits gratuits sont offerts lors de l'inscription. 
                Des crédits supplémentaires peuvent être achetés selon les tarifs en vigueur.
              </p>
            </section>

            {/* Cancellation */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Annulation et Résiliation</h2>
              <p className="mb-4">
                <strong>6.1 Annulation par l'utilisateur :</strong> Vous pouvez annuler votre abonnement à tout moment 
                depuis votre tableau de bord. L'annulation prendra effet à la fin de votre période de facturation en cours.
              </p>
              <p className="mb-4">
                <strong>6.2 Résiliation par Markeap :</strong> Nous nous réservons le droit de suspendre ou de résilier 
                votre compte en cas de violation de ces conditions ou d'utilisation abusive du service.
              </p>
            </section>

            {/* Usage */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Utilisation Acceptable</h2>
              <p className="mb-4">Vous vous engagez à NE PAS :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Utiliser le Service pour des activités illégales ou frauduleuses</li>
                <li>Envoyer du spam ou du contenu non sollicité</li>
                <li>Tenter d'accéder aux comptes d'autres utilisateurs</li>
                <li>Interférer avec le fonctionnement du Service</li>
                <li>Utiliser le Service pour harceler, menacer ou nuire à autrui</li>
                <li>Violer les droits de propriété intellectuelle</li>
              </ul>
            </section>

            {/* Data */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Propriété des Données</h2>
              <p className="mb-4">
                <strong>8.1 Vos données :</strong> Vous conservez tous les droits sur les données que vous importez 
                ou créez dans Markeap (informations clients, campagnes, etc.).
              </p>
              <p className="mb-4">
                <strong>8.2 Notre propriété intellectuelle :</strong> Markeap, son logo, ses fonctionnalités et 
                son code source sont la propriété de MY DIGITAL IDENTITY LTD et sont protégés par les lois sur 
                la propriété intellectuelle.
              </p>
            </section>

            {/* Warranty */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation de Garantie</h2>
              <p>
                Le Service est fourni "tel quel" et "selon disponibilité". Nous ne garantissons pas que le Service 
                sera ininterrompu, sans erreur ou exempt de virus. Nous nous efforçons de maintenir une disponibilité 
                maximale mais ne pouvons garantir un uptime de 100%.
              </p>
            </section>

            {/* Liability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation de Responsabilité</h2>
              <p>
                Dans la mesure permise par la loi, MY DIGITAL IDENTITY LTD ne sera pas responsable des dommages 
                indirects, accessoires, spéciaux ou consécutifs résultant de l'utilisation ou de l'impossibilité 
                d'utiliser le Service. Notre responsabilité totale ne dépassera pas le montant que vous avez payé 
                pour le Service au cours des 12 derniers mois.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications des Conditions</h2>
              <p>
                Nous nous réservons le droit de modifier ces Conditions à tout moment. Les modifications substantielles 
                vous seront notifiées par email ou via le tableau de bord. L'utilisation continue du Service après 
                les modifications constitue votre acceptation des nouvelles conditions.
              </p>
            </section>

            {/* Law */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Droit Applicable</h2>
              <p>
                Ces Conditions sont régies par les lois du Royaume-Uni. Tout litige sera soumis à la juridiction 
                exclusive des tribunaux de Londres, Royaume-Uni.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact</h2>
              <p className="mb-2">Pour toute question concernant ces Conditions, contactez-nous :</p>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <p className="font-semibold text-gray-900">MY DIGITAL IDENTITY LTD</p>
                <p className="text-gray-700">71-75 Shelton Street, Covent Garden</p>
                <p className="text-gray-700">London, United Kingdom, WC2H 9JQ</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;
